import * as React from 'react';
import { Field, FieldProps, FieldInputProps,  } from 'formik';
import { TextFieldWidget, TextFieldProps, TextAreaProps, TextAreaWidget } from '@tableau/widgets';

interface FormikTextFieldOwnProps {
  name: string;
  label: string;
  testId: string;
  validate?: (value: string) => string | undefined;
  className?: string;
  disableOnSubmit?: boolean;
  showValidationMessage?: boolean; // Configuration option for controlling showing validation message or not. Defaults to true
  eagerValidation?: boolean; // Configuration option for eager feedback on input validation. Default behavior- delay validation feedback until first blur. O/w if eagerValidationFeedback is true, provide feedback on very first keystroke.
  reserveVerticalSpaceForValidationMessage?: boolean; // Configuration option to reserve vertical space below input box to prevent jump off for the rest of the UI. Default behavior- no vertical space
}

interface TableauTextWidgetProps {
  autoComplete: 'on' | 'off';
  disabled: boolean;
  message: string;
  valid: boolean;
}

type InputAttrs = React.InputHTMLAttributes<HTMLInputElement>;
type InputAreaAttrs = React.TextareaHTMLAttributes<HTMLTextAreaElement>;
type InputAttrsOmitOnChange = Omit<InputAttrs, 'onChange'> & Omit<InputAreaAttrs, 'onChange'>;
export type FormikTextFieldInternalProps = Omit<FormikTextFieldOwnProps, 'label' | 'testId' | 'className'> & InputAttrsOmitOnChange;
export type FormikTextBoxProps = TextFieldProps & FormikTextFieldOwnProps & InputAttrsOmitOnChange & {ref?: React.RefObject<HTMLInputElement>};
export type FormikTextAreaProps = TextAreaProps & FormikTextFieldOwnProps & InputAttrsOmitOnChange;
type FormikFieldAndTextChildProps = {field: FieldInputProps<any>} & TableauTextWidgetProps;
type TextFieldChildFn = (props: FormikFieldAndTextChildProps) => React.ReactNode;

// Abstraction layer for <input type="text"> which mingles Formik Field with Shared Widgets' TextFieldWidget
// Best of both worlds: Formik's State management, error handling, etc and accessible Shared Widget
export const FormikTextField: React.FunctionComponent<FormikTextFieldInternalProps> = React.memo(function FormikTextField(
  props: FormikTextFieldInternalProps & {children: TextFieldChildFn}) {
  return (
    <Field name={props.name} type={props.type} validate={props.validate}>
      {(formikFieldProps: FieldProps) => {
        const tableauTextWidgetProps = getTextWidgetProps(formikFieldProps,
          props.disableOnSubmit ?? true,
          props.showValidationMessage ?? true,
          props.eagerValidation ?? false,
          props.reserveVerticalSpaceForValidationMessage ?? false);

        const propsForTextWidgets: FormikFieldAndTextChildProps = {field: formikFieldProps.field, ...tableauTextWidgetProps};
        return props.children(propsForTextWidgets);
      }}
    </Field>
  );
});


export const getTextWidgetProps  = function(formikFieldProps: FieldProps,
                                  disableOnSubmit: boolean,
                                  showValidationMessage: boolean,
                                  eagerValidation: boolean,
                                  reserveVerticalSpaceForValidationMessage: boolean): TableauTextWidgetProps {
  let tableauTextWidgetProps: TableauTextWidgetProps = {
    autoComplete: 'off',
    disabled: disableOnSubmit && formikFieldProps.form.isSubmitting,
    message: undefined,
    valid: undefined
  };
  if(showValidationMessage) {
    const eagerCondition: boolean = eagerValidation || formikFieldProps.meta.touched;
    tableauTextWidgetProps = {
      ...tableauTextWidgetProps,
      message: eagerCondition && formikFieldProps.meta.error || (reserveVerticalSpaceForValidationMessage ? '\u200B': undefined),
      valid: formikFieldProps.meta.error && eagerCondition ? false : undefined
    };
  }
  return tableauTextWidgetProps;
};

export const FormikTextBox = React.forwardRef(function FormikTextBox(props: FormikTextBoxProps, ref: React.RefObject<HTMLInputElement>) {
  return (
    <FormikTextField type={'text'} {...props}>{
      (fieldAndTextWidgetProps: FormikFieldAndTextChildProps) => {
        const {field, ...textWidgetProps} = fieldAndTextWidgetProps;
        const {validate, eagerValidation, disableOnSubmit, showValidationMessage, ...restOfFormikTextBoxProps} = props;
        return (
          <TextFieldWidget
            label={props.label}
            testId={props.testId}
            className={props.className}
            {...field}
            value={field.value ?? ''}
            {...textWidgetProps}
            {...restOfFormikTextBoxProps}
            ref={ref}
          />);
      }}
    </FormikTextField>
  );
});

export function FormikTextArea(props: FormikTextAreaProps) {
  return (
    <FormikTextField type={'textarea'} showValidationMessage={false} {...props}>{
      (fieldAndTextWidgetProps: FormikFieldAndTextChildProps) => {
        const {field, ...textWidgetProps} = fieldAndTextWidgetProps;
        const {eagerValidation, disableOnSubmit, showValidationMessage, validate, ...restOfFormikTextAreaProps} = props;
        return (
          <TextAreaWidget
            label={props.label}
            testId={props.testId}
            className={props.className}
            {...field}
            value={field.value ?? ''}
            {...textWidgetProps}
            {...restOfFormikTextAreaProps}
          />);
      }}
    </FormikTextField>
  );
}
